import React, { useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Search, Sparkles, ArrowRight, AlertTriangle, Check, Target, Eye, MessageSquare } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AIDemoSection = () => {
  const { t } = useTranslation();
  const [url, setUrl] = useState('');
  const [keyword, setKeyword] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [demoRemaining, setDemoRemaining] = useState(null);

  const handleAnalyze = async (e) => {
    e.preventDefault();
    if (!url || !keyword) return;
    setLoading(true);
    setResult(null);
    setError('');

    try {
      const res = await axios.post(`${API_URL}/api/ai/demo`, { url, keyword });
      setResult(res.data);
      if (res.data.demo_remaining !== undefined) {
        setDemoRemaining(res.data.demo_remaining);
      }
    } catch (err) {
      if (err.response?.status === 429) {
        setError(t('demo.errors.rateLimit'));
        setDemoRemaining(0);
      } else {
        setError(t('demo.errors.generic'));
      }
    } finally {
      setLoading(false);
    }
  };

  const getScoreColor = (score) => {
    if (score >= 70) return { text: '#10B981', bg: '#10B981' };
    if (score >= 40) return { text: '#F59E0B', bg: '#F59E0B' };
    return { text: '#EF4444', bg: '#EF4444' };
  };

  const getVisibilityLabel = (v) => {
    if (v === 'high') return { label: t('demo.visibility.high'), color: '#10B981' };
    if (v === 'medium') return { label: t('demo.visibility.medium'), color: '#F59E0B' };
    return { label: t('demo.visibility.low'), color: '#EF4444' };
  };

  return (
    <section id="demo" className="py-24 px-6 relative overflow-hidden" data-testid="ai-demo-section">
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-[#0055FF]/5 rounded-full blur-[120px]" />
      </div>

      <div className="max-w-4xl mx-auto relative z-10">
        <div className="text-center mb-10">
          <span className="text-xs uppercase tracking-[0.2em] text-[#0055FF] font-semibold">{t('demo.eyebrow')}</span>
          <h2 className="font-['Outfit'] text-4xl sm:text-5xl font-medium tracking-tight mt-4 mb-4">
            {t('demo.title')}
          </h2>
          <p className="text-base text-[#A1A1AA] max-w-2xl mx-auto">
            {t('demo.subtitle')}
            {demoRemaining !== null && (
              <span className="block mt-2 text-xs">
                {demoRemaining > 0
                  ? <span className="text-[#0055FF]">{demoRemaining} {t('demo.remaining')}</span>
                  : <span className="text-[#EF4444]">{t('demo.exhausted')}</span>}
              </span>
            )}
          </p>
        </div>

        <form onSubmit={handleAnalyze} className="max-w-2xl mx-auto mb-8">
          <div className="bg-[#111111] border border-white/10 rounded-2xl p-6 space-y-4">
            <div className="space-y-4">
              <div>
                <label className="text-xs text-[#A1A1AA] block mb-1.5 font-medium">{t('demo.urlLabel')}</label>
                <Input
                  value={url}
                  onChange={(e) => setUrl(e.target.value)}
                  placeholder={t('demo.urlPlaceholder')}
                  className="bg-[#0a0a0a] border-white/10 text-white h-12"
                  required
                  data-testid="demo-url-input"
                />
              </div>
              <div>
                <label className="text-xs text-[#A1A1AA] block mb-1.5 font-medium">{t('demo.keywordLabel')}</label>
                <Input
                  value={keyword}
                  onChange={(e) => setKeyword(e.target.value)}
                  placeholder={t('demo.keywordPlaceholder')}
                  className="bg-[#0a0a0a] border-white/10 text-white h-12"
                  required
                  data-testid="demo-keyword-input"
                />
              </div>
            </div>
            <Button
              type="submit"
              disabled={loading || !url || !keyword || demoRemaining === 0}
              className="w-full bg-[#0055FF] hover:bg-[#3377FF] text-white font-medium rounded-xl py-6 text-base"
              data-testid="demo-analyze-btn"
            >
              {loading ? (
                <span className="flex items-center gap-2">
                  <div className="w-5 h-5 border-2 border-white/20 border-t-white rounded-full animate-spin" />
                  {t('demo.loading')}
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  <Sparkles className="w-5 h-5" />
                  {t('demo.analyzeBtn')}
                </span>
              )}
            </Button>
          </div>
        </form>

        {error && (
          <div className="max-w-2xl mx-auto mb-8 p-4 rounded-xl bg-[#EF4444]/10 border border-[#EF4444]/30 text-[#EF4444] text-sm flex items-center gap-3" data-testid="demo-error">
            <AlertTriangle className="w-5 h-5 flex-shrink-0" />
            <span>{error}</span>
            <Link to="/register" className="text-[#0055FF] hover:underline whitespace-nowrap ml-auto text-xs">
              {t('demo.signUp')} <ArrowRight className="w-3 h-3 inline" />
            </Link>
          </div>
        )}

        {result && (
          <div className="max-w-2xl mx-auto" data-testid="demo-result">
            <div className="bg-[#111111] border border-white/10 rounded-2xl overflow-hidden">
              {/* Dual Score Header */}
              <div className="p-6 border-b border-white/5">
                <div className="grid grid-cols-2 gap-6">
                  {result.score !== undefined && (
                    <div className="flex items-center gap-4" data-testid="demo-seo-score">
                      <div className="w-16 h-16 rounded-2xl flex items-center justify-center flex-shrink-0"
                        style={{ backgroundColor: getScoreColor(result.score).bg + '15' }}>
                        <span className="text-2xl font-['Outfit'] font-bold" style={{ color: getScoreColor(result.score).text }}>
                          {result.score}
                        </span>
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <Search className="w-4 h-4 text-[#0055FF]" />
                          <h3 className="font-['Outfit'] font-medium">{t('demo.seoScore')}</h3>
                        </div>
                        <p className="text-xs text-[#A1A1AA] mt-1">
                          {result.score >= 70 ? t('demo.scoreGood') : result.score >= 40 ? t('demo.scoreImprove') : t('demo.scoreCritical')}
                        </p>
                      </div>
                    </div>
                  )}
                  {result.aeo && (
                    <div className="flex items-center gap-4" data-testid="demo-aeo-score">
                      <div className="w-16 h-16 rounded-2xl flex items-center justify-center flex-shrink-0"
                        style={{ backgroundColor: getScoreColor(result.aeo.aeo_score).bg + '15' }}>
                        <span className="text-2xl font-['Outfit'] font-bold" style={{ color: getScoreColor(result.aeo.aeo_score).text }}>
                          {result.aeo.aeo_score}
                        </span>
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <Sparkles className="w-4 h-4 text-[#8B5CF6]" />
                          <h3 className="font-['Outfit'] font-medium">{t('demo.aeoScore')}</h3>
                        </div>
                        <p className="text-xs text-[#A1A1AA] mt-1">
                          {t('demo.aiVisibility')}: <span style={{ color: getVisibilityLabel(result.aeo.ai_visibility).color }}>
                            {getVisibilityLabel(result.aeo.ai_visibility).label}
                          </span>
                        </p>
                      </div>
                    </div>
                  )}
                </div>

                {result.aeo && (
                  <div className="grid grid-cols-3 gap-3 mt-4 pt-4 border-t border-white/5">
                    <div className="text-center p-2 rounded-lg bg-white/5">
                      <Eye className="w-4 h-4 mx-auto mb-1 text-[#8B5CF6]" />
                      <p className="text-xs text-[#A1A1AA]">{t('demo.citability')}</p>
                      <p className="text-sm font-semibold" style={{ color: getScoreColor(result.aeo.citability_score).text }}>
                        {result.aeo.citability_score}/100
                      </p>
                    </div>
                    <div className="text-center p-2 rounded-lg bg-white/5">
                      <MessageSquare className="w-4 h-4 mx-auto mb-1 text-[#8B5CF6]" />
                      <p className="text-xs text-[#A1A1AA]">{t('demo.featuredSnippet')}</p>
                      <p className="text-sm font-semibold">
                        {result.aeo.featured_snippet_ready
                          ? <span className="text-[#10B981]">{t('demo.ready')}</span>
                          : <span className="text-[#EF4444]">{t('demo.no')}</span>}
                      </p>
                    </div>
                    <div className="text-center p-2 rounded-lg bg-white/5">
                      <Sparkles className="w-4 h-4 mx-auto mb-1 text-[#8B5CF6]" />
                      <p className="text-xs text-[#A1A1AA]">{t('demo.aiVisibilityShort')}</p>
                      <p className="text-sm font-semibold" style={{ color: getVisibilityLabel(result.aeo.ai_visibility).color }}>
                        {getVisibilityLabel(result.aeo.ai_visibility).label}
                      </p>
                    </div>
                  </div>
                )}
              </div>

              {/* SEO Issues */}
              {result.issues && result.issues.length > 0 && (
                <div className="p-6 border-b border-white/5">
                  <h4 className="text-xs uppercase tracking-wider text-[#A1A1AA] mb-3 flex items-center gap-2">
                    <AlertTriangle className="w-3 h-3" /> {t('demo.seoIssues')}
                  </h4>
                  <ul className="space-y-3">
                    {result.issues.map((issue, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm">
                        <span className="text-[#EF4444] mt-0.5 flex-shrink-0">&#x2022;</span>
                        <span className="text-white/80">
                          {typeof issue === 'string' ? issue : (issue.description || issue.message || '')}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* AEO Improvements */}
              {result.aeo?.improvements && result.aeo.improvements.length > 0 && (
                <div className="p-6 border-b border-white/5">
                  <h4 className="text-xs uppercase tracking-wider text-[#A1A1AA] mb-3 flex items-center gap-2">
                    <Sparkles className="w-3 h-3 text-[#8B5CF6]" /> {t('demo.aeoSuggestions')}
                  </h4>
                  <ul className="space-y-3">
                    {result.aeo.improvements.map((s, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm">
                        <span className="text-[#8B5CF6] mt-0.5 flex-shrink-0">&#x2022;</span>
                        <span className="text-white/80">
                          {typeof s === 'string' ? s.substring(0, 200) : (s.description || s.message || '')}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* SEO Suggestions */}
              {result.suggestions && result.suggestions.length > 0 && (
                <div className="p-6 border-b border-white/5">
                  <h4 className="text-xs uppercase tracking-wider text-[#A1A1AA] mb-3 flex items-center gap-2">
                    <Check className="w-3 h-3" /> {t('demo.seoSuggestions')}
                  </h4>
                  <ul className="space-y-3">
                    {result.suggestions.map((s, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm">
                        <span className="text-[#10B981] mt-0.5 flex-shrink-0">&#x2022;</span>
                        <span className="text-white/80">
                          {typeof s === 'string' ? s.substring(0, 200) : (s.description || s.message || '')}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Missing Keywords */}
              {result.missing_keywords && result.missing_keywords.length > 0 && (
                <div className="p-6 border-b border-white/5">
                  <h4 className="text-xs uppercase tracking-wider text-[#A1A1AA] mb-3 flex items-center gap-2">
                    <Target className="w-3 h-3" /> {t('demo.missingKeywords')}
                  </h4>
                  <div className="flex flex-wrap gap-2">
                    {result.missing_keywords.map((kw, i) => (
                      <span key={i} className="px-3 py-1 rounded-full text-xs bg-[#F59E0B]/10 text-[#F59E0B] border border-[#F59E0B]/20">
                        {kw}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Post-Result CTA */}
              <div className="p-6 bg-gradient-to-r from-[#0055FF]/10 to-[#8B5CF6]/10 border-t border-white/5">
                <div className="text-center">
                  <p className="text-base font-['Outfit'] font-medium mb-1">
                    {t('demo.cta.title')}
                  </p>
                  <p className="text-sm text-[#A1A1AA] mb-4">
                    {t('demo.cta.subtitle')}
                  </p>
                  <Link to="/register">
                    <Button className="bg-[#0055FF] hover:bg-[#3377FF] text-white rounded-full px-8 py-3 text-sm font-medium group" data-testid="demo-register-cta">
                      {t('demo.cta.button')}
                      <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
                    </Button>
                  </Link>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default AIDemoSection;
